import React, { useState } from "react";
import { motion } from "framer-motion";
import emailjs from "emailjs-com";
import {
  ArrowRight,
  BadgeCheck,
  Banknote,
  BarChart3,
  Boxes,
  BriefcaseBusiness,
  Building2,
  CalendarCheck,
  CheckCircle2,
  ClipboardList,
  FileText,
  Landmark,
  LockKeyhole,
  MessageSquareText,
  PackageCheck,
  PieChart,
  ReceiptText,
  ShieldCheck,
  Sparkles,
  Store,
  UsersRound,
  WalletCards,
} from "lucide-react";

import Footer from "./Footer";
import "./Home.css";

import heroImg from "../components/Images/icon colored 7.png";
import payroll from "../components/Images/payroll.png";
import headshot1 from "../components/Images/headshot1.jpeg";
import headshot2 from "../components/Images/headshot2.jpeg";
import headshot3 from "../components/Images/headshot3.jpeg";
import pricingImg from "../components/Images/pricing.png";
import reportImg from "../components/Images/report.png";
import collaboImg from "../components/Images/collabo.png";
import remoteImg from "../components/Images/remote-access.png";

const fadeInUp = {
  hidden: { opacity: 0, y: 22 },
  show: { opacity: 1, y: 0, transition: { duration: 0.55, ease: "easeOut" } },
};

const fadeInLeft = {
  hidden: { opacity: 0, x: -24 },
  show: { opacity: 1, x: 0, transition: { duration: 0.55, ease: "easeOut" } },
};

const fadeInRight = {
  hidden: { opacity: 0, x: 24 },
  show: { opacity: 1, x: 0, transition: { duration: 0.55, ease: "easeOut" } },
};

const stagger = {
  hidden: {},
  show: { transition: { staggerChildren: 0.08 } },
};

const quickBenefits = [
  "Send invoices",
  "Track expenses",
  "Manage payroll",
  "Monitor stock",
  "View reports",
  "Control user access",
];

const problems = [
  {
    icon: FileText,
    title: "No more scattered records",
    text: "Keep invoices, receipts, customers, vendors, expenses, payroll, and reports in one place.",
  },
  {
    icon: WalletCards,
    title: "Know your money position",
    text: "See what came in, what went out, who owes you, and what your business still needs to pay.",
  },
  {
    icon: UsersRound,
    title: "Work safely with your team",
    text: "Give staff access based on their roles so everyone sees only what they need.",
  },
  {
    icon: ShieldCheck,
    title: "Stay organised as you grow",
    text: "Move from manual spreadsheets to a simple finance workspace built for SMEs.",
  },
];

const featureCards = [
  {
    icon: ReceiptText,
    image: pricingImg,
    title: "Sales, invoices & receipts",
    text: "Create estimates, invoices, and receipts. Track paid, unpaid, pending, and overdue customer balances.",
    points: ["Branded PDFs", "Customer payments", "VAT/WHT support"],
  },
  {
    icon: Landmark,
    image: collaboImg,
    title: "Accounting & bookkeeping",
    text: "Keep proper business records with journals, ledgers, chart of accounts, and trial balance.",
    points: ["Journal entries", "General ledger", "Trial balance"],
  },
  {
    icon: BarChart3,
    image: reportImg,
    title: "Reports & business insight",
    text: "Understand your profit, cash flow, business position, receivables, and payables clearly.",
    points: ["Income statement", "Balance sheet", "Cash flow"],
  },
  {
    icon: UsersRound,
    image: remoteImg,
    title: "Team roles & access",
    text: "Invite users and control what Super Admins, Accountants, HR, Marketing, and Sales users can access.",
    points: ["User roles", "Cleaner sidebar", "Restricted access"],
  },
  {
    icon: UsersRound,
    image: payroll,
    title: "Employee payroll",
    text: "Manage employees, payroll runs, payslips, deductions, approvals, and payroll posting.",
    points: ["Payroll runs", "Payslips", "Approvals"],
  },
  {
    icon: Boxes,
    image: reportImg,
    title: "Inventory management",
    text: "Track products, warehouses, stock movement, inventory value, and ageing stock.",
    points: ["Stock tracking", "Warehouses", "Valuation"],
  },
];

const allModules = [
  {
    icon: PackageCheck,
    title: "Purchases & expenses",
    text: "Record vendor bills, operating costs, purchases, and supplier payments.",
  },
  {
    icon: Building2,
    title: "Fixed assets",
    text: "Track laptops, equipment, furniture, depreciation, and asset history.",
  },
  {
    icon: Banknote,
    title: "Bank reconciliation",
    text: "Compare bank transactions with your records and keep your books cleaner.",
  },
  {
    icon: BriefcaseBusiness,
    title: "CRM tool",
    text: "Manage customers, follow-ups, opportunities, and relationship activities.",
  },
  {
    icon: PieChart,
    title: "Budgeting, FP&A",
    text: "Plan budgets, compare actual results, and understand where money is going.",
  },
  {
    icon: CalendarCheck,
    title: "Booking & scheduling",
    text: "Manage appointments, schedules, tasks, and business activities.",
  },
  {
    icon: ClipboardList,
    title: "Tasks & approvals",
    text: "Assign tasks, comment, approve work, and track progress across the team.",
  },
  {
    icon: MessageSquareText,
    title: "AI assistant",
    text: "Coming soon: ask questions about your numbers in plain English.",
  },
];

const steps = [
  {
    title: "Set up your business",
    text: "Add your company details, team members, customers, vendors, products, and accounts.",
  },
  {
    title: "Run daily transactions",
    text: "Create invoices, record expenses, manage purchases, payroll, inventory, and payments.",
  },
  {
    title: "Track what matters",
    text: "Monitor outstanding invoices, stock levels, cash movement, business costs, and approvals.",
  },
  {
    title: "Make better decisions",
    text: "Use reports to understand profit, cash flow, assets, liabilities, and business performance.",
  },
];

const roles = [
  "Super Admin",
  "Accountant",
  "Sales/User",
  "HR User",
  "Marketing User",
];

const audiences = [
  "SMEs",
  "Retail stores",
  "Consultants",
  "Agencies",
  "Startups",
  "NGOs",
  "Professional service firms",
  "Growing finance teams",
];

const faqs = [
  {
    q: "What is KostPadi?",
    a: "KostPadi is a simple business finance platform that helps SMEs manage invoices, accounting, payroll, inventory, customers, vendors, reports, and team access in one place.",
  },
  {
    q: "Do I need to be an accountant to use it?",
    a: "No. KostPadi is built to be simple for business owners while still giving accountants the proper tools they need.",
  },
  {
    q: "Can my staff use KostPadi too?",
    a: "Yes. You can invite team members and assign roles like Super Admin, Accountant, Sales/User, HR User, and Marketing User.",
  },
  {
    q: "Does KostPadi support Nigerian business needs?",
    a: "Yes. It supports practical Nigerian SME workflows including VAT, WHT, invoices, receipts, payroll, customer balances, vendor payments, and financial reports.",
  },
];

export default function Home() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(null);
  const [openFaq, setOpenFaq] = useState(0);

  const handleChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const sendEmail = async (e) => {
    e.preventDefault();
    setSending(true);
    setSent(null);

    try {
      await emailjs.send(
        "YOUR_SERVICE_ID",
        "YOUR_TEMPLATE_ID",
        {
          from_name: form.name,
          reply_to: form.email,
          message: form.message,
        },
        "YOUR_PUBLIC_KEY"
      );

      setSent("ok");
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      console.error(err);
      setSent("err");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="home-root">
      <header className="home-hero" id="top">
        <div className="container home-hero-grid">
          <motion.div
            variants={stagger}
            initial="hidden"
            animate="show"
            className="home-hero-copy"
          >
            <motion.span variants={fadeInUp} className="home-tag">
              ERP for modern Nigerian SMEs
            </motion.span>

            <motion.h1 variants={fadeInUp}>
              Manage your business finance without the confusion.
            </motion.h1>

            <motion.p variants={fadeInUp} className="home-lead">
              KostPadi helps you create invoices, track expenses, manage
              customers, run payroll, monitor inventory, generate reports, and
              work with your team — all from one simple platform.
            </motion.p>

            <motion.div variants={fadeInUp} className="home-hero-actions">
              <a className="home-btn home-btn-primary" href="/login">
                Get Started Free <ArrowRight size={18} />
              </a>
              <a className="home-btn home-btn-ghost" href="/features">
                Explore Features
              </a>
            </motion.div>

            <motion.div variants={fadeInUp} className="home-hero-pills">
              {quickBenefits.map((item) => (
                <span key={item}>
                  <CheckCircle2 size={16} />
                  {item}
                </span>
              ))}
            </motion.div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: 18 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 0.65, ease: "easeOut" }}
            className="home-hero-visual"
          >
            <img src={heroImg} alt="KostPadi business finance platform" />
            <div className="home-floating-card card-a">
              <BadgeCheck size={18} />
              <span>Business records organised</span>
            </div>
            <div className="home-floating-card card-b">
              <BarChart3 size={18} />
              <span>Reports made simple</span>
            </div>
          </motion.div>
        </div>

        <div className="home-marquee">
          <div className="home-marquee-track">
            {[
              "Invoices",
              "Receipts",
              "Payroll",
              "Inventory",
              "Reports",
              "CRM",
              "Budgeting",
              "Banking",
              "Team Roles",
              "Approvals",
              "Fixed Assets",
              "Bookings",
              "Invoices",
              "Receipts",
              "Payroll",
              "Inventory",
              "Reports",
              "CRM",
              "Budgeting",
              "Banking",
              "Team Roles",
              "Approvals",
              "Fixed Assets",
              "Bookings",
            ].map((item, idx) => (
              <span key={`${item}-${idx}`}>{item}</span>
            ))}
          </div>
        </div>
      </header>

      <section className="section container">
        <motion.div
          variants={stagger}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          className="home-problem-grid"
        >
          {problems.map(({ icon: Icon, title, text }) => (
            <motion.div
              variants={fadeInUp}
              className="home-problem-card"
              key={title}
            >
              <div className="home-icon-wrap">
                <Icon size={22} />
              </div>
              <h3>{title}</h3>
              <p>{text}</p>
            </motion.div>
          ))}
        </motion.div>
      </section>

      <section id="features" className="section container">
        <motion.div
          variants={stagger}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
        >
          <motion.div variants={fadeInUp} className="home-section-head center">
            <span className="home-mini-tag">What you can do with KostPadi</span>
            <h2>
              One platform for your daily finance and business operations.
            </h2>
            <p>
              Built for business owners who want clarity, and finance teams who
              need proper records.
            </p>
          </motion.div>

          <div className="home-feature-grid">
            {featureCards.map(({ icon: Icon, image, title, text, points }) => (
              <motion.div
                variants={fadeInUp}
                className="home-feature-card"
                key={title}
              >
                <div className="home-feature-img">
                  <img src={image} alt={title} />
                </div>

                <div className="home-feature-body">
                  <div className="home-feature-icon">
                    <Icon size={20} />
                  </div>
                  <h3>{title}</h3>
                  <p>{text}</p>

                  <ul>
                    {points.map((point) => (
                      <li key={point}>
                        <CheckCircle2 size={16} />
                        {point}
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      <section className="section home-section-alt">
        <div className="container home-split">
          <motion.div
            variants={fadeInLeft}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            className="home-split-copy"
          >
            <span className="home-mini-tag">How it works</span>
            <h2>From daily transactions to clear business reports.</h2>
            <p>
              KostPadi helps you move from guesswork to proper visibility. You
              record what happens in the business, then use the reports to
              understand what it means.
            </p>

            <div className="home-steps">
              {steps.map((step, idx) => (
                <div className="home-step" key={step.title}>
                  <span>{idx + 1}</span>
                  <div>
                    <h3>{step.title}</h3>
                    <p>{step.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>

          <motion.div
            variants={fadeInRight}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            className="home-split-visual"
          >
            <img src={reportImg} alt="KostPadi reports and dashboard" />
          </motion.div>
        </div>
      </section>

      <section className="section container">
        <div className="home-section-head">
          <span className="home-mini-tag">More useful tools</span>
          <h2>Everything your growing business needs to stay organised.</h2>
          <p>
            KostPadi is more than invoicing. It brings core SME operations into
            one connected workspace.
          </p>
        </div>

        <motion.div
          variants={stagger}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          className="home-module-grid"
        >
          {allModules.map(({ icon: Icon, title, text }) => (
            <motion.div
              variants={fadeInUp}
              className="home-module-card"
              key={title}
            >
              <div className="home-icon-wrap soft">
                <Icon size={22} />
              </div>
              <h3>{title}</h3>
              <p>{text}</p>
            </motion.div>
          ))}
        </motion.div>
      </section>

      <section className="section home-section-alt">
        <div className="container home-role-wrap">
          <div className="home-section-head center">
            <span className="home-mini-tag">Multi-user access</span>
            <h2>Give the right access to the right people.</h2>
            <p>
              Your accountant, sales team, HR, and marketing team should not all
              see the same things. KostPadi keeps access clean and practical.
            </p>
          </div>

          <div className="home-role-grid">
            {roles.map((role) => (
              <div className="home-role-card" key={role}>
                <LockKeyhole size={19} />
                <span>{role}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section container">
        <div className="home-section-head center">
          <span className="home-mini-tag">Who KostPadi is for</span>
          <h2>Built for businesses that want better control.</h2>
        </div>

        <div className="home-audience-grid">
          {audiences.map((audience) => (
            <span key={audience}>
              <Store size={17} />
              {audience}
            </span>
          ))}
        </div>
      </section>

      <section className="section home-section-alt">
        <div className="container">
          <div className="home-ai-card">
            <div>
              <span className="home-mini-tag coming">Coming soon</span>
              <h2>AI Business Assistant</h2>
              <p>
                Soon, you’ll be able to ask KostPadi questions like “Who owes me
                the most?”, “How much profit did I make?”, or “Which expense is
                increasing?” and get simple answers from your business records.
              </p>
            </div>

            <div className="home-ai-list">
              <span>
                <Sparkles size={16} />
                Plain-English questions
              </span>
              <span>
                <MessageSquareText size={16} />
                Smart summaries
              </span>
              <span>
                <BarChart3 size={16} />
                Useful business insights
              </span>
            </div>
          </div>
        </div>
      </section>

      <section className="section container">
        <div className="home-section-head center">
          <span className="home-mini-tag">Why teams choose KostPadi</span>
          <h2>Less manual work. Cleaner records. Better decisions.</h2>
        </div>

        <div className="home-metrics">
          <div className="home-metric">
            <span className="home-metric-value">1</span>
            <span className="home-metric-label">workspace for finance</span>
          </div>
          <div className="home-metric">
            <span className="home-metric-value">5+</span>
            <span className="home-metric-label">user role types</span>
          </div>
          <div className="home-metric">
            <span className="home-metric-value">24/7</span>
            <span className="home-metric-label">cloud access</span>
          </div>
          <div className="home-metric">
            <span className="home-metric-value">SME</span>
            <span className="home-metric-label">friendly workflows</span>
          </div>
        </div>
      </section>

      <section className="section home-section-alt">
        <div className="container">
          <h2 className="home-section-title">
            Loved by founders & finance teams
          </h2>

          <div className="home-testimonials">
            <div className="home-testimonial">
              <img src={headshot1} alt="Client testimonial" />
              <p>“Billing is finally predictable.”</p>
              <span>Nadia • Ops Lead</span>
            </div>

            <div className="home-testimonial">
              <img src={headshot2} alt="Client testimonial" />
              <p>“Estimate to invoice saves us hours.”</p>
              <span>Tunde • Project Manager</span>
            </div>

            <div className="home-testimonial">
              <img src={headshot3} alt="Client testimonial" />
              <p>“Built for how we work in Nigeria.”</p>
              <span>Ijeoma • Founder</span>
            </div>
          </div>
        </div>
      </section>

      <section id="get-started" className="section container">
        <div className="home-cta-card">
          <div>
            <h2>Ready to make your business finance easier?</h2>
            <p>
              Start free, invite your team, and upgrade when your business needs
              more.
            </p>
          </div>

          <div className="home-cta-actions">
            <a className="home-btn home-btn-primary" href="/login">
              Start Free <ArrowRight size={18} />
            </a>
            <a className="home-btn home-btn-ghost light" href="/pricing">
              See Plans
            </a>
          </div>
        </div>
      </section>

      <section className="section home-section-alt">
        <div className="container">
          <div className="home-section-head center">
            <span className="home-mini-tag">Questions</span>
            <h2>Frequently asked questions</h2>
          </div>

          <div className="home-faq">
            {faqs.map((f, idx) => (
              <div
                key={f.q}
                className={`home-faq-item ${openFaq === idx ? "open" : ""}`}
                onClick={() => setOpenFaq(openFaq === idx ? -1 : idx)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) =>
                  e.key === "Enter" && setOpenFaq(openFaq === idx ? -1 : idx)
                }
              >
                <div className="home-faq-q">{f.q}</div>
                <div className="home-faq-a">
                  <p>{f.a}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="contact" className="section container">
        <div className="home-contact-wrap">
          <div className="home-contact-copy">
            <span className="home-mini-tag">Need help?</span>
            <h2>Let’s help you set up</h2>
            <p>
              Questions about pricing, features, or onboarding your team? Send
              us a message.
            </p>
          </div>

          <form className="home-contact-form" onSubmit={sendEmail}>
            <label>
              Name
              <input
                name="name"
                type="text"
                required
                value={form.name}
                onChange={handleChange}
                placeholder="Your name"
              />
            </label>

            <label>
              Email
              <input
                name="email"
                type="email"
                required
                value={form.email}
                onChange={handleChange}
                placeholder="name@email.com"
              />
            </label>

            <label>
              Message
              <textarea
                name="message"
                rows="4"
                required
                value={form.message}
                onChange={handleChange}
                placeholder="Tell us what you need…"
              />
            </label>

            <button
              className="home-btn home-btn-primary"
              type="submit"
              disabled={sending}
            >
              {sending ? "Sending…" : "Send message"}
            </button>

            {sent === "ok" && (
              <p className="form-success">
                Thanks! We’ll get back to you shortly.
              </p>
            )}

            {sent === "err" && (
              <p className="form-error">
                Something went wrong. Please try again.
              </p>
            )}
          </form>
        </div>
      </section>

      <Footer />
    </div>
  );
}
